import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { resetPassword } from "../../../core/data/redux/adminAuthSlice";
import Toasts from "../../../core/common/toast";
import "../login/employeeLogin.css";

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

const schema = Yup.object().shape({
  new_password: Yup.string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      passwordRegex,
      "Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character"
    ),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("new_password"), undefined], "Passwords must match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const employeeAuthState = useSelector(
    (state: any) => state.adminAuthState
  );



  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: any) => {
    data["employee_id"] = employeeAuthState?.user?.data?.data?.employee_id;
    data["token"] = employeeAuthState?.user?.data?.data?.token;
    dispatch(resetPassword(data));
  };

  useEffect(() => {
    if (
      employeeAuthState.loading === "fulfilled" &&
      employeeAuthState.action === "reset-password"
    ) {
      navigation(routes.adminResetPasswordSuccess);
    }
  }, [employeeAuthState.loading]);

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const [password, setPassword] = useState("");
  const [passwordResponce, setPasswordResponce] = useState({
    passwordResponceText:
      "Use 8 or more characters with a mix of letters, numbers, and symbols.",
    passwordResponceKey: "",
  });

  const togglePasswordVisibility = (field: "password" | "confirmPassword") => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const onChangePassword = (password: string) => {
    setPassword(password);
    if (password.match(/^$|\s+/)) {
      setPasswordResponce({
        passwordResponceText:
          "Use 8 or more characters with a mix of letters, numbers & symbols",
        passwordResponceKey: "",
      });
    } else if (password.length === 0) {
      setPasswordResponce({
        passwordResponceText: "",
        passwordResponceKey: "",
      });
    } else if (password.length < 8) {
      setPasswordResponce({
        passwordResponceText: "Weak. Must contain at least 8 characters",
        passwordResponceKey: "0",
      });
    } else if (
      password.search(/[a-z]/) < 0 ||
      password.search(/[A-Z]/) < 0 ||
      password.search(/[0-9]/) < 0
    ) {
      setPasswordResponce({
        passwordResponceText:
          "Average. Must contain at least 1 upper case and number",
        passwordResponceKey: "1",
      });
    } else if (password.search(/(?=.*?[#?!@$%^&*-])/) < 0) {
      setPasswordResponce({
        passwordResponceText: "Almost. Must contain a special symbol",
        passwordResponceKey: "2",
      });
    } else {
      setPasswordResponce({
        passwordResponceText: "Awesome! You have a secure password.",
        passwordResponceKey: "3",
      });
    }
  };

  return (
    <>
      <Toasts
        show={employeeAuthState.loading === "rejected"}
        message={employeeAuthState.error}
        title={"Reset Password Failed"}
        type={"danger"}
      />
      <Toasts
        show={employeeAuthState.loading === "fulfilled"}
        message={employeeAuthState?.user?.data?.message}
        title={"Success"}
        type={"success"}
      />
      <div className="hero">
        <div className="sub-hero">
          <div className="img-logo">
            <img src="/assets/img/login/logo.png" alt="logoimage" />
          </div>
          <p className="text-white fs-3 fw-bold sub-title text-center">
            Reset Password
          </p>
          <p className="text-white text-center mb-4">
            Your new password must be different from previous used passwords.
          </p>
          {/* Form */}
          <div className="">
            <form onSubmit={handleSubmit(onSubmit)} className="form-container">
              <div className="form-group">
                <label htmlFor="new_password" className="">
                  New Password
                </label>
                <div className="password-input-container">
                  <input
                    type={passwordVisibility.password ? "text" : "password"}
                    id="new_password"
                    className={`form-input ${
                      errors.new_password ? "error" : ""
                    }`}
                    placeholder="Enter your password"
                    maxLength={15}
                    {...register("new_password")}
                    onChange={(e) => {
                      onChangePassword(e.target.value);
                      register("new_password").onChange(e);
                    }}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility("password")}
                  >
                    {passwordVisibility.password ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-eye-icon lucide-eye"
                        >
                          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-eye-off-icon lucide-eye-off"
                        >
                          <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                          <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                          <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                          <path d="m2 2 20 20" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
                {errors.new_password && (
                  <span className="error-message">
                    {errors.new_password.message}
                  </span>
                )}
                <div
                  className={`password-strength d-flex ${
                    passwordResponce.passwordResponceKey === "0"
                      ? "poor-active"
                      : passwordResponce.passwordResponceKey === "1"
                      ? "avg-active"
                      : passwordResponce.passwordResponceKey === "2"
                      ? "strong-active"
                      : passwordResponce.passwordResponceKey === "3"
                      ? "heavy-active"
                      : ""
                  }`}
                  id="passwordStrength"
                  style={{ marginTop: "8px" }}
                >
                  <span id="poor" className="active" />
                  <span id="weak" className="active" />
                  <span id="strong" className="active" />
                  <span id="heavy" className="active" />
                </div>
                <p
                  className="text-white"
                  style={{ fontSize: "12px", marginTop: "4px" }}
                >
                  {passwordResponce.passwordResponceText}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="confirm_password" className="">
                  Confirm Password
                </label>
                <div className="password-input-container">
                  <input
                    type={
                      passwordVisibility.confirmPassword ? "text" : "password"
                    }
                    id="confirm_password"
                    className={`form-input ${
                      errors.confirm_password ? "error" : ""
                    }`}
                    placeholder="Confirm your password"
                    maxLength={15}
                    {...register("confirm_password")}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility("confirmPassword")}
                  >
                    {passwordVisibility.confirmPassword ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-eye-icon lucide-eye"
                        >
                          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-eye-off-icon lucide-eye-off"
                        >
                          <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                          <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                          <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                          <path d="m2 2 20 20" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
                {errors.confirm_password && (
                  <span className="error-message">
                    {errors.confirm_password.message}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={employeeAuthState.loading === "pending"}
              >
                {employeeAuthState.loading === "pending"
                  ? "Resetting..."
                  : "Reset Password"}
              </button>
              <p className="terms-text">
                By clicking on Reset Password, you agree to our Terms of service
                and Privacy Policy
              </p>
            </form>
          </div>
        </div>
        <div className="right-img">
          <div className="ladies-img">
            <img src="/assets/img/login/ladies.png" alt="" />
          </div>
          <div className="metro-img">
            <img src="/assets/img/login/metroo.png" alt="" />
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
